import type { PublicPromotion } from '@/lib/api/content';
import type { Banner } from '@/lib/api/admin-banners';

export type Campaign = {
  key: string;
  slug: string;
  title: string;
  heading: string[];
  eyebrow: string;
  note: string;
  /** Admin banner images: the slide becomes a banner without a caption or terms. */
  desktop?: string;
  mobile?: string;
  /** Promotion cover uploaded in admin: replaces only the artwork behind the caption. */
  artDesktop?: string;
  artMobile?: string;
  link?: string;
  offer?: PublicPromotion;
  customHeading?: boolean;
};

const art: Record<
  string,
  { key: string; approvedTitle: string; heading: string[]; eyebrow: string }
> = {
  first_order: {
    approvedTitle: 'Ролл за первый заказ!',
    key: 'first-order',
    heading: ['Первый заказ.', 'С подарком.'],
    eyebrow: 'Приятно познакомиться',
  },
  status_rolls: {
    approvedTitle: 'Статусные роллы',
    key: 'status',
    heading: ['Больше вкуса.', 'В подарок.'],
    eyebrow: 'Статусные роллы',
  },
  loyalty: {
    approvedTitle: 'Кэшбэк до 7%',
    key: 'loyalty',
    heading: ['Любовь', 'возвращается.'],
    eyebrow: 'Коины за заказы',
  },
  review: {
    approvedTitle: 'Вы нам отзыв, мы вам ролл!',
    key: 'review',
    heading: ['Вы — отзыв.', 'Мы — ролл.'],
    eyebrow: 'Твоё мнение важно',
  },
};

export type CoverFormat = 'desktop' | 'mobile';

export const coverAsset = (key: string, format: CoverFormat) =>
  `/storefront/campaign-v6-${key}-${format}.webp`;

/** Banner image, then uploaded promotion art unless it failed to load, then the local asset. */
export function coverSource(
  campaign: Campaign,
  format: CoverFormat,
  failed: ReadonlySet<string> = new Set()
) {
  const uploaded = format === 'mobile' ? campaign.artMobile : campaign.artDesktop;
  return (
    (format === 'mobile' ? campaign.mobile : campaign.desktop) ||
    (uploaded && !failed.has(uploaded) ? uploaded : '') ||
    coverAsset(campaign.key, format)
  );
}

export function safeBannerLink(link?: string) {
  if (!link) return '';
  if (/^\/(?!\/)/.test(link) || /^#[\w-]+$/.test(link)) return link;
  try {
    const url = new URL(link);
    return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
  } catch {
    return '';
  }
}

export function buildCampaigns(promotions: PublicPromotion[], banners: Banner[]): Campaign[] {
  const offers = [...promotions]
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((offer) => {
      const presentation = art[offer.slug];
      return {
        key: presentation?.key || 'first-order',
        slug: offer.slug,
        title: offer.title,
        heading: presentation?.approvedTitle === offer.title ? presentation.heading : [offer.title],
        eyebrow: presentation?.eyebrow || 'Акции Азиаточки',
        note: offer.description,
        artDesktop: offer.web_image_desktop || undefined,
        artMobile: offer.web_image_mobile || undefined,
        offer,
        customHeading: presentation?.approvedTitle !== offer.title,
      };
    });
  // Admin banners remain a live channel. Never freeze their URLs or discard an
  // unfamiliar new campaign just because it did not exist in the design preview.
  // Only banners marked for the site reach the hero: without the flag (an older
  // backend) a banner is an app banner and its portrait art does not fit here.
  const managed = banners
    .filter((b) => b.is_active !== false && (b.show_on_web === 1 || b.show_on_web === true))
    .map((banner) => ({
      key: `managed-${banner.id}`,
      slug: `banner-${banner.id}`,
      title: banner.title || 'Азиаточка',
      heading: [],
      eyebrow: '',
      note: '',
      desktop: banner.image_desktop,
      mobile: banner.image_mobile || banner.image_desktop,
      link: safeBannerLink(banner.link),
    }));
  return [...offers, ...managed];
}
